import type { MultilingualQuestion } from '../../types/multilingualQuestion';

// Multilingual version of Lane Changing questions
export const laneChangingMultilingual: MultilingualQuestion[] = [
  {
    id: 'q-lc-1',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/lane-changing/lane-changing-1-solid-white-line.png',
    text: {
      en: 'You are on a road with a solid white line between your lane and the left lane. What does this mean?',
      nl: 'Je bent op een weg met een ononderbroken witte lijn tussen je rijstrook en de linkse strook. Wat betekent dit?',
      ar: 'أنت على طريق به خط أبيض متصل بين ممرك والممر الأيسر. ماذا يعني هذا؟'
    },
    options: [
      {
        id: 'q-lc-1o1',
        text: {
          en: 'You may cross it to overtake when safe',
          nl: 'Je mag het oversteken om in te halen wanneer het veilig is',
          ar: 'يمكنك عبوره للتجاوز عندما يكون آمنا'
        }
      },
      {
        id: 'q-lc-1o2',
        text: {
          en: 'You must not cross this line except to avoid a hazard',
          nl: 'Je mag deze lijn niet oversteken, behalve om een gevaar te vermijden',
          ar: 'يجب عليك عدم عبور هذا الخط إلا لتجنب خطر'
        }
      },
      {
        id: 'q-lc-1o3',
        text: {
          en: 'You may cross it freely at any time',
          nl: 'Je mag het op elk moment vrij oversteken',
          ar: 'يمكنك عبوره بحرية في أي وقت'
        }
      }
    ],
    correctAnswerId: 'q-lc-1o2',
    explanation: {
      en: 'A solid white line marks the edge of a lane and prohibits crossing except when necessary to avoid a hazard or obstruction. Do not change lanes across a solid white line unless it is essential for safety. (RVV 1990 art. 64)',
      nl: 'Een ononderbroken witte lijn markeert de rand van een rijstrook en verbiedt het oversteken, behalve wanneer nodig om een gevaar of obstructie te vermijden. Verander van rijstrook niet over een ononderbroken witte lijn, tenzij dit noodzakelijk is voor veiligheid. (RVV 1990 art. 64)',
      ar: 'يحدد خط أبيض متصل حافة الممر ويحظر العبور إلا عند الضرورة لتجنب خطر أو عائق. لا تقم بتغيير المسارات عبر خط أبيض متصل إلا إذا كان ذلك ضروريا للسلامة. (RVV 1990 art. 64)'
    },
    subject: 'Lane Changing'
  },
  {
    id: 'q-lc-2',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/lane-changing/lane-changing-2-dashed-white-line.png',
    text: {
      en: 'You are on a road with a dashed white line between your lane and the next. What does this permit?',
      nl: 'Je bent op een weg met een onderbroken witte lijn tussen je rijstrook en de volgende. Wat staat dit toe?',
      ar: 'أنت على طريق به خط أبيض متقطع بين ممرك والممر التالي. ماذا يسمح به هذا؟'
    },
    options: [
      {
        id: 'q-lc-2o1',
        text: {
          en: 'You must change lanes immediately',
          nl: 'Je moet onmiddellijk van rijstrook veranderen',
          ar: 'يجب عليك تغيير المسار فورا'
        }
      },
      {
        id: 'q-lc-2o2',
        text: {
          en: 'You may change lanes when it is safe and you have checked your mirrors',
          nl: 'Je mag van rijstrook veranderen wanneer het veilig is en je je spiegels hebt gecontroleerd',
          ar: 'يمكنك تغيير المسار عندما يكون آمنا وتحقق من مرآتك'
        }
      },
      {
        id: 'q-lc-2o3',
        text: {
          en: 'You must signal but do not need to check mirrors',
          nl: 'Je moet signaleren maar hoeft je spiegels niet te controleren',
          ar: 'يجب عليك الإشارة ولكن لا تحتاج إلى التحقق من المرآة'
        }
      }
    ],
    correctAnswerId: 'q-lc-2o2',
    explanation: {
      en: 'A dashed white line permits lane changes when safe. You must still check mirrors, signal, and look over your shoulder to check for vehicles in your blind spot before changing lanes. (RVV 1990 art. 64)',
      nl: 'Een onderbroken witte lijn staat van rijstrookwisseling toe wanneer het veilig is. Je moet nog steeds je spiegels controleren, signaleren en over je schouder kijken om voertuigen in je dodehoek te controleren voordat je van rijstrook verandert. (RVV 1990 art. 64)',
      ar: 'يسمح خط أبيض متقطع بتغيير المسار عندما يكون آمنا. يجب عليك التحقق من المرايا والإشارة والنظر فوق كتفك للتحقق من المركبات في نقطة العمى قبل تغيير المسار. (RVV 1990 art. 64)'
    },
    subject: 'Lane Changing'
  },
  {
    id: 'q-lc-3',
    text: {
      en: 'What is the correct procedure for changing lanes safely?',
      nl: 'Wat is de juiste procedure voor veilig van rijstrook veranderen?',
      ar: 'ما هي الإجراء الصحيح لتغيير المسار بأمان؟'
    },
    options: [
      {
        id: 'q-lc-3o1',
        text: {
          en: 'Check mirrors, signal, check blind spot, then change when safe',
          nl: 'Controleer spiegels, signaleer, controleer dodehoek, verandeer dan wanneer veilig',
          ar: 'تحقق من المرايا، أشر، تحقق من نقطة العمى، ثم تغيير عندما يكون آمنا'
        }
      },
      {
        id: 'q-lc-3o2',
        text: {
          en: 'Signal and change immediately',
          nl: 'Signaleer en verander onmiddellijk',
          ar: 'أشر وتغيير فورا'
        }
      },
      {
        id: 'q-lc-3o3',
        text: {
          en: 'Change lanes quickly without signaling',
          nl: 'Verander snel van rijstrook zonder te signaleren',
          ar: 'غير المسار بسرعة دون الإشارة'
        }
      }
    ],
    correctAnswerId: 'q-lc-3o1',
    explanation: {
      en: 'The correct Dutch CBR lane-changing procedure is: (1) check interior and side mirrors, (2) signal your intention, (3) check your blind spot by looking over your shoulder, and (4) change lanes smoothly when safe. This is the spiegel-teken-rijden sequence. (RVV 1990 art. 42, 43)',
      nl: 'De juiste CBR-rijstrookwisselingsprocedure is: (1) controleer binnen- en zijspiegels, (2) signaleer je voornemen, (3) controleer je dodehoek door over je schouder te kijken, en (4) verander soepel van rijstrook wanneer het veilig is. Dit is de spiegel-teken-rijden volgorde. (RVV 1990 art. 42, 43)',
      ar: 'الإجراء الصحيح لتغيير المسار وفق CBR الهولندي هو: (1) تحقق من مرايا داخلية وجانبية، (2) أشر لنيتك، (3) تحقق من نقطة العمى بالنظر فوق كتفك، و (4) غير المسار بسلاسة عندما يكون آمنا. هذا هو تسلسل سبيجل-تيكن-ريدن. (RVV 1990 art. 42, 43)'
    },
    subject: 'Lane Changing'
  },
  {
    id: 'q-lc-4',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/lane-changing/lane-changing-4-heavy-rain.png',
    text: {
      en: 'You want to change lanes in heavy rain. Why must you take extra care?',
      nl: 'Je wilt van rijstrook veranderen in zware regen. Waarom moet je extra voorzichtig zijn?',
      ar: 'تريد تغيير المسار في أمطار غزيرة. لماذا يجب أن تكون حذرا بشكل إضافي؟'
    },
    options: [
      {
        id: 'q-lc-4o1',
        text: {
          en: 'Rain makes your mirrors clearer',
          nl: 'Regen maakt je spiegels duidelijker',
          ar: 'تجعل الأمطار مرآتك أوضح'
        }
      },
      {
        id: 'q-lc-4o2',
        text: {
          en: 'Reduced visibility and grip mean other vehicles are harder to see and braking is less effective',
          nl: 'Verminderde zichtbaarheid en grip betekenen dat andere voertuigen moeilijker te zien zijn en remmen minder effectief',
          ar: 'تعني الرؤية والقبضة المنخفضة أن المركبات الأخرى أصعب في الرؤية والفرامل أقل فعالية'
        }
      },
      {
        id: 'q-lc-4o3',
        text: {
          en: 'Rain has no effect on lane changing',
          nl: 'Regen heeft geen effect op rijstrookwisseling',
          ar: 'الأمطار ليس لها تأثير على تغيير المسار'
        }
      }
    ],
    correctAnswerId: 'q-lc-4o2',
    explanation: {
      en: 'Heavy rain reduces visibility and road grip. Other vehicles are harder to spot, and both you and other drivers have reduced braking performance. Increase your following distance, check more carefully before changing lanes, and do not make sudden movements. (RVV 1990 art. 10)',
      nl: 'Zware regen vermindert zichtbaarheid en weggreep. Andere voertuigen zijn moeilijker op te spotten, en zowel jij als andere chauffeurs hebben verminderde remcapaciteit. Vergroot je volafstand, controleer zorgvuldiger voordat je van rijstrook verandert, en maak geen plotselinge bewegingen. (RVV 1990 art. 10)',
      ar: 'تقلل الأمطار الغزيرة من الرؤية والقبضة على الطريق. يصعب رصد المركبات الأخرى، وكل من أنت والسائقين الآخرين لديهم أداء فرملة منخفض. زيادة المسافة، تحقق بعناية أكثر قبل تغيير المسار، ولا تقم بحركات مفاجئة. (RVV 1990 art. 10)'
    },
    subject: 'Lane Changing'
  },
  {
    id: 'q-lc-5',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/lane-changing/lane-changing-5-night-driving.png',
    text: {
      en: 'You are driving at night and want to change lanes. What extra precaution should you take?',
      nl: 'Je rijdt \'s nachts en wilt van rijstrook veranderen. Welke extra voorzorgsmaatregel moet je nemen?',
      ar: 'أنت تقود ليلا وتريد تغيير المسار. ما هو الاحتياط الإضافي الذي يجب عليك اتخاذه؟'
    },
    options: [
      {
        id: 'q-lc-5o1',
        text: {
          en: 'Flash your lights instead of using mirrors',
          nl: 'Knippeer met je lichten in plaats van spiegels te gebruiken',
          ar: 'اومض بأضواءك بدلا من استخدام المرايا'
        }
      },
      {
        id: 'q-lc-5o2',
        text: {
          en: 'Change lanes more quickly to minimize time in the other lane',
          nl: 'Verander sneller van rijstrook om de tijd in de andere strook te minimaliseren',
          ar: 'تغيير المسار بسرعة أكبر لتقليل الوقت في المسار الآخر'
        }
      },
      {
        id: 'q-lc-5o3',
        text: {
          en: 'Check your blind spot more carefully — visibility is reduced and you may not see vehicles',
          nl: 'Controleer je dodehoek zorgvuldiger — de zichtbaarheid is verminderd en je kunt voertuigen niet zien',
          ar: 'تحقق من نقطة العمى بعناية أكبر — الرؤية منخفضة وقد لا ترى المركبات'
        }
      }
    ],
    correctAnswerId: 'q-lc-5o3',
    explanation: {
      en: 'At night, visibility is severely reduced. Your mirrors may not show all approaching vehicles, especially unlit motorcycles or bicycles. Check your blind spot more thoroughly by looking over your shoulder. Use dipped headlights to avoid dazzling other drivers. (RVV 1990 art. 66)',
      nl: 'Immers is de zichtbaarheid ernstig verminderd. Je spiegels geven mogelijk niet alle naderende voertuigen weer, vooral onverlichte motorfietsen of fietsen. Controleer je dodehoek grondiger door over je schouder te kijken. Gebruik \'s dimlicht om andere bestuurders niet te verblinden. (RVV 1990 art. 66)',
      ar: 'في الليل، تكون الرؤية منخفضة بشكل كبير. قد لا تظهر مرآتك جميع المركبات القادمة، خاصة الدراجات النارية أو الدراجات غير المضاءة. تحقق من نقطة العمى بعمق أكثر بالنظر فوق كتفك. استخدم المصابيح المنخفضة لتجنب إرباك السائقين الآخرين. (RVV 1990 art. 66)'
    },
    subject: 'Lane Changing'
  },
  {
    id: 'q-lc-6',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/lane-changing/lane-changing-6-blind-spot-danger.png',
    text: {
      en: 'Before changing lanes, you check your mirror but see nothing. Is it safe to change lanes?',
      nl: 'Voordat je van rijstrook verandert, controleer je je spiegel maar je ziet niets. Is het veilig om van rijstrook te veranderen?',
      ar: 'قبل تغيير المسار، تتحقق من مرآتك لكنك لا ترى شيئا. هل من الآمن تغيير المسار؟'
    },
    options: [
      {
        id: 'q-lc-6o1',
        text: {
          en: 'Yes, mirrors show everything',
          nl: 'Ja, spiegels laten alles zien',
          ar: 'نعم، تظهر المرايا كل شيء'
        }
      },
      {
        id: 'q-lc-6o2',
        text: {
          en: 'No — you must also check your blind spot by looking over your shoulder',
          nl: 'Nee — je moet ook je dodehoek controleren door over je schouder te kijken',
          ar: 'لا — يجب عليك أيضا التحقق من نقطة العمى بالنظر فوق كتفك'
        }
      },
      {
        id: 'q-lc-6o3',
        text: {
          en: 'Only if the road is quiet',
          nl: 'Alleen als de weg rustig is',
          ar: 'فقط إذا كان الطريق هادئا'
        }
      }
    ],
    correctAnswerId: 'q-lc-6o2',
    explanation: {
      en: 'Mirrors do not show everything — there are areas beside and slightly behind your vehicle (blind spots) that mirrors cannot see. Before every lane change, physically check your blind spot by turning your head and looking over your shoulder. This is a legal requirement. (RVV 1990 art. 42)',
      nl: 'Spiegels tonen niet alles — er zijn gebieden naast en iets achter je voertuig (dodehoeken) die spiegels niet kunnen zien. Controleer voordat je van rijstrook verandert je dodehoek fysiek door je hoofd te draaien en over je schouder te kijken. Dit is een wettelijke verplichting. (RVV 1990 art. 42)',
      ar: 'لا تظهر المرايا كل شيء — هناك مناطق بجانب وقليلا خلف سيارتك (نقاط عمياء) التي لا تستطيع المرايا رؤيتها. قبل كل تغيير مسار، تحقق من نقطة العمى جسديا بتحويل رأسك والنظر فوق كتفك. هذا متطلب قانوني. (RVV 1990 art. 42)'
    },
    subject: 'Lane Changing'
  },
  {
    id: 'q-lc-7',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/lane-changing/lane-changing-7-bus-merge.png',
    text: {
      en: 'You are behind a bus that is signaling to change lanes and merge. What should you do?',
      nl: 'Je bent achter een bus die signaleert om van rijstrook te veranderen en in te voegen. Wat moet je doen?',
      ar: 'أنت خلف حافلة تشير لتغيير المسار والدمج. ماذا يجب أن تفعل؟'
    },
    options: [
      {
        id: 'q-lc-7o1',
        text: {
          en: 'Speed up to pass before it moves',
          nl: 'Versnel om het voorbij te gaan voordat het beweegt',
          ar: 'تسريع للمرور قبل أن يتحرك'
        }
      },
      {
        id: 'q-lc-7o2',
        text: {
          en: 'Slow down and give way — allow the bus to merge safely',
          nl: 'Vertraag en geef voorrang — laat de bus veilig invoegen',
          ar: 'أبطئ وأعط الأولوية — اسمح للحافلة بالدمج بأمان'
        }
      },
      {
        id: 'q-lc-7o3',
        text: {
          en: 'Flash your lights to signal you will not move',
          nl: 'Knippeer met je lichten om te signaleren dat je niet beweegt',
          ar: 'اومض بأضواءك لتشير إلى أنك لن تتحرك'
        }
      }
    ],
    correctAnswerId: 'q-lc-7o2',
    explanation: {
      en: 'When a bus signals to change lanes or merge from a stop, you should give way and allow it to move safely. Buses are large and need space; forcing them to wait creates hazards. Anticipate their movements and adjust your speed accordingly. (RVV 1990 art. 50)',
      nl: 'Wanneer een bus signaleert om van rijstrook te veranderen of in te voegen uit een stop, moet je voorrang geven en het veilig laten bewegen. Bussen zijn groot en hebben ruimte nodig; hen laten wachten veroorzaakt gevaren. Voorstel hun bewegingen en pas je snelheid dienovereenkomstig aan. (RVV 1990 art. 50)',
      ar: 'عندما تشير حافلة لتغيير المسار أو الدمج من محطة، يجب عليك إعطاء الأولوية والسماح لها بالتحرك بأمان. الحافلات كبيرة وتحتاج إلى مساحة؛ إجبارهم على الانتظار يخلق مخاطر. توقع حركاتهم واضبط سرعتك وفقا لذلك. (RVV 1990 art. 50)'
    },
    subject: 'Lane Changing'
  },
  {
    id: 'q-lc-8',
    text: {
      en: 'Which mirrors must you check before changing lanes on a motorway?',
      nl: 'Welke spiegels moet je controleren voordat je op de snelweg van rijstrook verandert?',
      ar: 'ما هي المرايا التي يجب عليك التحقق منها قبل تغيير المسار على الطريق السريع؟'
    },
    options: [
      {
        id: 'q-lc-8o1',
        text: {
          en: 'Only the side mirrors',
          nl: 'Alleen de zijspiegels',
          ar: 'فقط مرايا الجانب'
        }
      },
      {
        id: 'q-lc-8o2',
        text: {
          en: 'Interior mirror and side mirrors; also check your blind spot',
          nl: 'Binnenspiegel en zijspiegels; controleer ook je dodehoek',
          ar: 'مرآة داخلية ومرايا جانبية؛ تحقق أيضا من نقطة العمى'
        }
      },
      {
        id: 'q-lc-8o3',
        text: {
          en: 'Mirrors are not needed if you check your blind spot',
          nl: 'Spiegels zijn niet nodig als je je dodehoek controleert',
          ar: 'المرايا غير مطلوبة إذا تحققت من نقطة العمى'
        }
      }
    ],
    correctAnswerId: 'q-lc-8o2',
    explanation: {
      en: 'Always check your interior (rearview) mirror to see vehicles behind you, and both side mirrors to check the lane you plan to enter. Then physically check your blind spot by looking over your shoulder. This three-step check is essential before any lane change. (RVV 1990 art. 42)',
      nl: 'Controleer altijd je binnenspiegel (achteruitkijkspiegel) om voertuigen achter je te zien, en beide zijspiegels om de strook die je wilt invoegen te controleren. Controleer dan fysiek je dodehoek door over je schouder te kijken. Deze driestapaanpak is essentieel vóór elke rijstrookwisseling. (RVV 1990 art. 42)',
      ar: 'تحقق دائما من مرآتك الداخلية (مرآة الرؤية الخلفية) لرؤية المركبات خلفك، ومرايا الجانب للتحقق من الممر الذي تخطط للدخول إليه. ثم تحقق جسديا من نقطة العمى بالنظر فوق كتفك. هذا الفحص ثلاثي المراحل ضروري قبل أي تغيير مسار. (RVV 1990 art. 42)'
    },
    subject: 'Lane Changing'
  },
  {
    id: 'q-lc-9',
    text: {
      en: 'What is the purpose of signaling before you change lanes?',
      nl: 'Wat is het doel van signaleren voordat je van rijstrook verandert?',
      ar: 'ما هو الغرض من الإشارة قبل تغيير المسار؟'
    },
    options: [
      {
        id: 'q-lc-9o1',
        text: {
          en: 'To make your car easier to see',
          nl: 'Om je auto gemakkelijker zichtbaar te maken',
          ar: 'لجعل سيارتك أسهل في الرؤية'
        }
      },
      {
        id: 'q-lc-9o2',
        text: {
          en: 'To warn other road users of your intention and give them time to react',
          nl: 'Om andere weggebruikers van je voornemen te waarschuwen en hen tijd te geven om te reageren',
          ar: 'لتحذير مستخدمي الطريق الآخرين من نيتك وإعطاؤهم وقتا للتفاعل'
        }
      },
      {
        id: 'q-lc-9o3',
        text: {
          en: 'Signaling is optional if no one is nearby',
          nl: 'Signaleren is optioneel als niemand dichtbij is',
          ar: 'الإشارة اختيارية إذا لم يكن أحد قريبا'
        }
      }
    ],
    correctAnswerId: 'q-lc-9o2',
    explanation: {
      en: 'Your signal informs other drivers and cyclists of your intention to change lanes, giving them time to react and allowing them to avoid collisions. Always signal your intention, even if you believe the road is clear — use your indicator lights. (RVV 1990 art. 42)',
      nl: 'Je signaal informeert andere bestuurders en fietsers van je voornemen om van rijstrook te veranderen, wat hen tijd geeft om te reageren en hen in staat stelt botsingen te vermijden. Signaleer altijd je voornemen, zelfs als je denkt dat de weg leeg is — gebruik je richtingaanwijzers. (RVV 1990 art. 42)',
      ar: 'تخبر إشارتك السائقين الآخرين والدراجين بنيتك لتغيير المسار، مما يعطيهم الوقت للتفاعل ويسمح لهم بتجنب الاصطدامات. أشر دائما لنيتك، حتى لو كنت تعتقد أن الطريق واضح — استخدم مؤشرات الاتجاه. (RVV 1990 art. 42)'
    },
    subject: 'Lane Changing'
  },
  {
    id: 'q-lc-10',
    text: {
      en: 'You see an oncoming vehicle in the lane you plan to enter. When may you change lanes?',
      nl: 'Je ziet een tegemoetkomend voertuig in de strook die je wilt invoegen. Wanneer mag je van rijstrook veranderen?',
      ar: 'ترى مركبة قادمة في الممر الذي تخطط للدخول إليه. متى يمكنك تغيير المسار؟'
    },
    options: [
      {
        id: 'q-lc-10o1',
        text: {
          en: 'Immediately — you have right of way',
          nl: 'Onmiddellijk — je hebt voorrang',
          ar: 'فورا — لديك الأولوية'
        }
      },
      {
        id: 'q-lc-10o2',
        text: {
          en: 'Wait until the oncoming vehicle has passed and the lane is completely clear',
          nl: 'Wacht tot het tegemoetkomend voertuig is gepasseerd en de strook volledig vrij is',
          ar: 'انتظر حتى تمر المركبة القادمة والممر خالي تماما'
        }
      },
      {
        id: 'q-lc-10o3',
        text: {
          en: 'Flash your lights to warn the other driver',
          nl: 'Knippeer met je lichten om de andere bestuurder te waarschuwen',
          ar: 'اومض بأضواءك لتحذير السائق الآخر'
        }
      }
    ],
    correctAnswerId: 'q-lc-10o2',
    explanation: {
      en: 'You must wait until the lane is clear before changing. Never force your way into an occupied lane. The approaching vehicle has the right to use that lane. Wait, maintain your distance, and only change when it is absolutely safe to do so without forcing other road users to brake or swerve. (RVV 1990 art. 42, 43)',
      nl: 'Je moet wachten tot de strook vrij is voordat je verandert. Dwing nooit je weg in een bezette strook. Het tegemoetkomend voertuig heeft het recht om die strook te gebruiken. Wacht, houd je afstand, en verander alleen wanneer het volkomen veilig is zonder andere weggebruikers tot remmen of uitwijken te dwingen. (RVV 1990 art. 42, 43)',
      ar: 'يجب عليك الانتظار حتى يكون الممر واضحا قبل التغيير. لا تفرض طريقك في ممر مشغول. المركبة القادمة لها الحق في استخدام هذا الممر. انتظر، حافظ على المسافة، وتغيير فقط عندما يكون آمنا تماما دون إجبار مستخدمي الطريق الآخرين على الفرملة أو الانحراف. (RVV 1990 art. 42, 43)'
    },
    subject: 'Lane Changing'
  }
];
